/**
 * PricingTable - Tableau comparatif complet des fonctionnalités (dark theme)
 */

import { Check, X, Infinity } from "lucide-react";
import { cn } from "@/lib/utils";

// Catégories et features avec les données EXACTES du prompt
const COMPARISON_DATA = {
  "Gestion des chantiers": {
    icon: "📊",
    features: [
      {
        name: "Chantiers actifs simultanés",
        starter: "5",
        pro: "15",
        entreprise: "Illimité",
      },
      {
        name: "Devis par chantier",
        starter: true,
        pro: true,
        entreprise: true,
      },
      {
        name: "Factures fournisseurs",
        starter: true,
        pro: true,
        entreprise: true,
      },
      {
        name: "Frais annexes",
        starter: true,
        pro: true,
        entreprise: true,
      },
    ],
  },
  "Gestion des équipes": {
    icon: "👷",
    features: [
      {
        name: "Membres d'équipe",
        starter: "10",
        pro: "30",
        entreprise: "Illimité",
      },
      {
        name: "Affectations par demi-journée",
        starter: true,
        pro: true,
        entreprise: true,
      },
      {
        name: "Calcul coût main d'œuvre automatique",
        starter: true,
        pro: true,
        entreprise: true,
      },
      {
        name: "Gestion des absences",
        starter: true,
        pro: true,
        entreprise: true,
      },
    ],
  },
  "Intelligence Artificielle": {
    icon: "🤖",
    features: [
      {
        name: "Analyses IA / mois",
        starter: "60",
        pro: "240",
        entreprise: "Illimité",
      },
      {
        name: "Scans IA / mois",
        starter: "100",
        pro: "450",
        entreprise: "Illimité",
      },
      {
        name: "Recommandations personnalisées",
        starter: true,
        pro: true,
        entreprise: true,
      },
      {
        name: "Détection anomalies",
        starter: false,
        pro: true,
        entreprise: true,
      },
    ],
  },
  "Alertes & Prédictions": {
    icon: "⚠️",
    features: [
      {
        name: "Jour Critique",
        starter: true,
        pro: true,
        entreprise: true,
      },
      {
        name: "Alertes marge faible",
        starter: true,
        pro: true,
        entreprise: true,
      },
      {
        name: "Alertes retard",
        starter: true,
        pro: true,
        entreprise: true,
      },
      {
        name: "Alertes budget dépassé",
        starter: true,
        pro: true,
        entreprise: true,
      },
    ],
  },
  "Rapports & Exports": {
    icon: "📈",
    features: [
      {
        name: "Rapports PDF",
        starter: true,
        pro: true,
        entreprise: true,
      },
      {
        name: "Export Excel/CSV",
        starter: false,
        pro: true,
        entreprise: true,
      },
      {
        name: "Historique données",
        starter: "2 ans",
        pro: "5 ans",
        entreprise: "Illimité",
      },
    ],
  },
  Support: {
    icon: "🛟",
    features: [
      {
        name: "Email",
        starter: "48h",
        pro: "24h",
        entreprise: "Prioritaire",
      },
      {
        name: "Chat",
        starter: false,
        pro: true,
        entreprise: true,
      },
      {
        name: "Téléphone",
        starter: false,
        pro: false,
        entreprise: true,
      },
      {
        name: "Temps de réponse garanti",
        starter: false,
        pro: "24h",
        entreprise: "4h",
      },
    ],
  },
};

type FeatureValue = boolean | string;

function renderValue(value: FeatureValue, isPopular: boolean = false) {
  if (typeof value === "boolean") {
    if (value) {
      return (
        <div className="flex items-center justify-center">
          <div
            className={cn(
              "h-6 w-6 rounded-full flex items-center justify-center",
              isPopular ? "bg-[#10B981]/20" : "bg-[#10B981]/10"
            )}
          >
            <Check className="h-4 w-4 text-[#10B981]" />
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center">
        <div className="h-6 w-6 rounded-full flex items-center justify-center bg-white/[0.03]">
          <X className="h-4 w-4 text-white/20" />
        </div>
      </div>
    );
  }

  // String value
  if (value === "Illimité") {
    return (
      <div className="flex items-center justify-center gap-1.5">
        <Infinity className="h-4 w-4 text-[#F59E0B]" />
        <span className="text-sm font-medium text-[#F59E0B]">Illimité</span>
      </div>
    );
  }

  return <span className="text-sm font-medium text-white">{value}</span>;
}

export function PricingTable() {
  return (
    <section id="comparison-table" className="relative py-24">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/4 w-[600px] h-[400px] bg-[#1A365D]/5 rounded-full blur-[150px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Comparez les fonctionnalités en détail
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto">
            Toutes les fonctionnalités dont vous avez besoin pour piloter vos
            chantiers efficacement.
          </p>
        </div>

        {/* Table Desktop */}
        <div className="hidden lg:block max-w-5xl mx-auto">
          <div className="rounded-2xl border border-white/[0.05] overflow-hidden bg-[#0F0F14]/50 backdrop-blur-sm">
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-4 p-4 bg-white/[0.02] border-b border-white/[0.05] sticky top-0 z-10">
              <div className="text-sm font-medium text-white/40">
                Fonctionnalité
              </div>
              <div className="text-center">
                <span className="text-sm font-semibold text-white">Starter</span>
                <p className="text-xs text-white/40 mt-0.5">49€ HT/mois</p>
              </div>
              <div className="text-center relative pt-4">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-[#D4AF37] text-[8px] font-bold text-[#0A0A0F] uppercase">
                  Populaire
                </div>
                <span className="text-sm font-semibold text-[#F59E0B]">Pro</span>
                <p className="text-xs text-white/40 mt-0.5">99€ HT/mois</p>
              </div>
              <div className="text-center">
                <span className="text-sm font-semibold text-white">
                  Entreprise
                </span>
                <p className="text-xs text-white/40 mt-0.5">149€ HT/mois</p>
              </div>
            </div>

            {/* Table Body */}
            {Object.entries(COMPARISON_DATA).map(
              ([category, { icon, features }], categoryIndex) => (
                <div key={category}>
                  {/* Category Header */}
                  <div className="grid grid-cols-4 gap-4 p-4 bg-white/[0.02]">
                    <div className="flex items-center gap-2 col-span-4">
                      <span className="text-lg">{icon}</span>
                      <span className="text-sm font-semibold text-white">
                        {category}
                      </span>
                    </div>
                  </div>

                  {/* Features */}
                  {features.map((feature, featureIndex) => (
                    <div
                      key={feature.name}
                      className={cn(
                        "grid grid-cols-4 gap-4 p-4 transition-colors hover:bg-white/[0.02]",
                        featureIndex < features.length - 1 &&
                          "border-b border-white/[0.03]"
                      )}
                    >
                      <div className="text-sm text-white/60">{feature.name}</div>
                      <div className="text-center">
                        {renderValue(feature.starter)}
                      </div>
                      <div className="text-center bg-[#F59E0B]/[0.03] -mx-2 px-2">
                        {renderValue(feature.pro, true)}
                      </div>
                      <div className="text-center">
                        {renderValue(feature.entreprise)}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>

        {/* Table Mobile - Cards */}
        <div className="lg:hidden space-y-6">
          {Object.entries(COMPARISON_DATA).map(
            ([category, { icon, features }]) => (
              <div
                key={category}
                className="rounded-xl border border-white/[0.05] overflow-hidden bg-[#0F0F14]/50"
              >
                {/* Category Header */}
                <div className="flex items-center gap-2 p-4 bg-white/[0.02] border-b border-white/[0.05]">
                  <span className="text-lg">{icon}</span>
                  <span className="text-sm font-semibold text-white">
                    {category}
                  </span>
                </div>

                {/* Features */}
                <div className="divide-y divide-white/[0.03]">
                  {features.map((feature) => (
                    <div key={feature.name} className="p-4">
                      <p className="text-sm text-white/80 mb-3">{feature.name}</p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="space-y-1">
                          <p className="text-[10px] text-white/30 uppercase">
                            Starter
                          </p>
                          {renderValue(feature.starter)}
                        </div>
                        <div className="space-y-1 bg-[#F59E0B]/[0.05] -mx-1 px-1 py-1 rounded">
                          <p className="text-[10px] text-[#F59E0B] uppercase font-medium">
                            Pro
                          </p>
                          {renderValue(feature.pro, true)}
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] text-white/30 uppercase">
                            Entreprise
                          </p>
                          {renderValue(feature.entreprise)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </section>
  );
}

export default PricingTable;
